import React, { useState } from 'react';

import { ReactComponent as AlertIcon } from '../../../assets/icons/alert-icon.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import { ISO_COUNTRY_CODES, isValidCountryCode } from '../../../shared-functions/CountryCodes';
import getErrorMessage from '../../../shared-functions/getErrorMessage';
import ocpiApi from '../../../utils/api/ocpi';

interface RegisterPartnerModalProperties {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ourPartyId: string;
  ourCountryCode: string;
  ourRole: string;
}

/**
 * Validate form inputs based on current mode and return error object
 * @param {any} data - The current form data
 * @param {any} type - Registration mode ('SENDER' or 'RECEIVER')
 * @returns {any} Validation errors map
 */
const validateForm = (
  data: {
    theirVersionsUrl: string;
    theirTokenA: string;
    counterPartyId: string;
    counterCountryCode: string;
    counterRole: string;
  },
  type: 'SENDER' | 'RECEIVER',
): Record<string, string> => {
  const validationErrors: Record<string, string> = {};

  const partyId = data.counterPartyId.trim();
  if (!partyId) {
    validationErrors.counterPartyId = 'Counter Party ID is required.';
  } else if (partyId.length !== 3) {
    validationErrors.counterPartyId = 'Counter Party ID must be exactly 3 characters.';
  }

  const countryCode = data.counterCountryCode.trim();
  if (!countryCode) {
    validationErrors.counterCountryCode = 'Country Code is required.';
  } else if (!isValidCountryCode(countryCode)) {
    validationErrors.counterCountryCode =
      'Invalid Country Code. Must be a valid 2-letter ISO 3166-1 alpha-2 code.';
  }

  if (!data.counterRole) {
    validationErrors.counterRole = 'Partner Role is required.';
  }

  if (type === 'SENDER') {
    const url = data.theirVersionsUrl.trim();
    if (!url) {
      validationErrors.theirVersionsUrl = 'Partner Versions URL is required.';
    } else {
      try {
        const parsedUrl = new URL(url);
        if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
          validationErrors.theirVersionsUrl = 'Versions URL must use HTTP or HTTPS protocol.';
        }
      } catch {
        validationErrors.theirVersionsUrl =
          'Invalid URL format (e.g. https://partner.com/ocpi/versions).';
      }
    }

    const token = data.theirTokenA.trim();
    if (!token) {
      validationErrors.theirTokenA = 'Partner Token A is required.';
    }
  }

  return validationErrors;
};

/**
 * OCPI Register Partner Modal component
 * @param {any} props - Component props
 * @param {any} props.open - Open state
 * @param {any} props.onClose - Close callback
 * @param {any} props.onSuccess - Success callback
 * @param {any} props.ourPartyId - Our party ID
 * @param {any} props.ourCountryCode - Our country code
 * @param {any} props.ourRole - Our role
 * @returns {any} Rendered component
 */
function RegisterPartnerModal({
  open,
  onClose,
  onSuccess,
  ourPartyId,
  ourCountryCode,
  ourRole,
}: RegisterPartnerModalProperties): React.ReactElement {
  const dispatch = useAppDispatch();
  const [isVersionURLCopied, setIsVersionURLCopied] = useState<boolean>(false);
  const [isTokenACopied, setIsTokenACopied] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [registrationType, setRegistrationType] = useState<'SENDER' | 'RECEIVER'>('SENDER');
  const [generatedData, setGeneratedData] = useState<{
    tokenA: string;
    versionsUrl: string;
  } | null>(null);

  const defaultCounterRole = ourRole.toUpperCase() === 'CPO' ? 'EMSP' : 'CPO';

  const [formData, setFormData] = useState({
    theirVersionsUrl: '',
    theirTokenA: '',
    counterPartyId: '',
    counterCountryCode: '',
    counterRole: defaultCounterRole,
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);

  const isFormValid =
    registrationType === 'SENDER'
      ? !!(
          formData.counterPartyId.trim() &&
          formData.counterCountryCode.trim() &&
          formData.theirVersionsUrl.trim() &&
          formData.theirTokenA.trim()
        )
      : !!(formData.counterPartyId.trim() && formData.counterCountryCode.trim());

  /**
   * Handle input change and clear previous API errors
   * @param {any} name - Input name
   * @param {any} value - Input value
   */
  const handleInputChange = (name: string, value: string) => {
    setApiError(null);
    let formatted = value;
    if (name === 'counterPartyId') {
      formatted = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 3);
    } else if (name === 'counterCountryCode') {
      formatted = value.trim().toUpperCase();
    }

    const updatedFormData = { ...formData, [name]: formatted };
    setFormData(updatedFormData);

    if (touched[name] || formatted) {
      setTouched((previous) => ({ ...previous, [name]: true }));
      const validationErrors = validateForm(updatedFormData, registrationType);
      setErrors((previous) => ({ ...previous, [name]: validationErrors[name] || '' }));
    }
  };

  /**
   * Handle input blur event to trigger validation
   * @param {any} name - Input name
   */
  const handleBlur = (name: string) => {
    setTouched((previous) => ({ ...previous, [name]: true }));
    const validationErrors = validateForm(formData, registrationType);
    setErrors((previous) => ({ ...previous, [name]: validationErrors[name] || '' }));
  };

  /**
   * Handle registration mode change
   * @param {any} mode - Registration mode ('SENDER' or 'RECEIVER')
   */
  const handleModeChange = (mode: 'SENDER' | 'RECEIVER') => {
    setRegistrationType(mode);
    setErrors({});
    setTouched({});
    setApiError(null);
  };

  /**
   * Handle copy
   * @param {any} text - Text to copy
   * @param {any} label - Label
   * @param {any} setCopied - State setter callback
   */
  const handleCopy = (
    text: string,
    label: string,
    setCopied: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    navigator.clipboard.writeText(text);

    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 1000);

    dispatch(
      setNotificationMessage({
        message: `${label} copied to clipboard!`,
        type: 'success',
      }),
    );
  };

  /**
   * Handle submit
   */
  const handleSubmit = async () => {
    setApiError(null);
    const validationErrors = validateForm(formData, registrationType);

    const allTouched: Record<string, boolean> = {
      counterPartyId: true,
      counterCountryCode: true,
      counterRole: true,
    };
    if (registrationType === 'SENDER') {
      allTouched.theirVersionsUrl = true;
      allTouched.theirTokenA = true;
    }
    setTouched(allTouched);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      dispatch(
        setNotificationMessage({
          message: 'Please resolve all field errors before submitting.',
          type: 'error',
        }),
      );
      return;
    }

    setLoading(true);
    try {
      if (registrationType === 'SENDER') {
        await ocpiApi.registerAsSender({
          ...formData,
          ourPartyId,
          ourRole,
        });
        dispatch(setNotificationMessage({ message: 'Handshake successful', type: 'success' }));
        onSuccess();
        onClose();
      } else {
        const response = await ocpiApi.generateTokenA({
          ourPartyId,
          ourCountryCode,
          ourRole,
          counterPartyId: formData.counterPartyId,
          counterCountryCode: formData.counterCountryCode,
          counterRole: formData.counterRole,
        });

        // Since it's wrapped in Axios, data is usually inside response.data
        const responseData = response.data?.data || response.data || response;

        setGeneratedData({
          tokenA: responseData.tokenA || 'ERROR_GENERATING_TOKEN',
          versionsUrl: responseData.versionsUrl || 'ERROR_GENERATING_URL',
        });
        dispatch(
          setNotificationMessage({ message: 'Invitation token generated', type: 'success' }),
        );
        onSuccess();
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Operation failed');
      setApiError(errorMessage);

      const lowerMessage = errorMessage.toLowerCase();
      if (
        lowerMessage.includes('token') ||
        lowerMessage.includes('credential') ||
        lowerMessage.includes('unauthorized') ||
        lowerMessage.includes('match') ||
        lowerMessage.includes('invalid') ||
        lowerMessage.includes('401')
      ) {
        setErrors((previous) => ({
          ...previous,
          theirTokenA:
            lowerMessage.includes('token') ||
            lowerMessage.includes('credential') ||
            lowerMessage.includes('match') ||
            lowerMessage.includes('unauthorized')
              ? 'Token A mismatch or invalid credentials for partner.'
              : previous.theirTokenA,
          theirVersionsUrl:
            lowerMessage.includes('url') || lowerMessage.includes('connect')
              ? 'Invalid or unreachable partner Versions URL.'
              : previous.theirVersionsUrl,
        }));
        setTouched((previous) => ({
          ...previous,
          theirTokenA: true,
          theirVersionsUrl: true,
        }));
      }

      dispatch(
        setNotificationMessage({
          message: errorMessage,
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Render form content
   * @returns {any} Rendered element
   */
  const renderFormContent = () => {
    let buttonLabel = 'Generate Token A';
    if (loading) {
      buttonLabel = 'Processing...';
    } else if (registrationType === 'SENDER') {
      buttonLabel = 'Connect Partner';
    }

    if (generatedData) {
      return (
        <div className="rpm-token-result">
          <p className="rpm-token-result__desc">
            Share these details with your partner so they can initiate the handshake:
          </p>
          <div className="rpm-token-result__box">
            <div className="rpm-token-result__row">
              <span className="rpm-token-result__label">Versions URL</span>
              <div className="rpm-token-result__value-wrap">
                <code className="rpm-token-result__code">{generatedData.versionsUrl}</code>
                <MyButton
                  label={isVersionURLCopied ? 'Copied!' : 'Copy'}
                  buttonType="outline"
                  onClickFunc={() =>
                    handleCopy(generatedData.versionsUrl, 'Versions URL', setIsVersionURLCopied)
                  }
                />
              </div>
            </div>
            <div className="rpm-token-result__row">
              <span className="rpm-token-result__label">Token A</span>
              <div className="rpm-token-result__value-wrap">
                <code className="rpm-token-result__code">{generatedData.tokenA}</code>
                <MyButton
                  label={isTokenACopied ? 'Copied!' : 'Copy'}
                  buttonType="outline"
                  onClickFunc={() => handleCopy(generatedData.tokenA, 'Token A', setIsTokenACopied)}
                />
              </div>
            </div>
          </div>
          <p className="rpm-token-result__note">
            This token is temporary and will expire once the handshake completes.
          </p>
          <div className="rpm-token-result__actions">
            <MyButton label="Close" buttonType="primary" onClickFunc={onClose} />
          </div>
        </div>
      );
    }

    return (
      <div className="register-partner-form">
        {/* Mode toggle */}
        <div className="rpm-type-toggle">
          <button
            type="button"
            className={`rpm-type-btn${registrationType === 'SENDER' ? ' active' : ''}`}
            onClick={() => handleModeChange('SENDER')}>
            I am the Sender (Connect)
          </button>
          <button
            type="button"
            className={`rpm-type-btn${registrationType === 'RECEIVER' ? ' active' : ''}`}
            onClick={() => handleModeChange('RECEIVER')}>
            I am the Receiver (Invite)
          </button>
        </div>

        {/* API Error / Credential mismatch banner */}
        {apiError && (
          <div className="rpm-error-banner" role="alert">
            <AlertIcon className="rpm-error-banner__icon" />
            <div className="rpm-error-banner__content">
              <span className="rpm-error-banner__title">Registration Error</span>
              <span className="rpm-error-banner__msg">{apiError}</span>
            </div>
          </div>
        )}

        {/* Partner details (always shown) */}
        <div className="rpm-section">
          <h3 className="rpm-section__title">Partner Details</h3>
          <div className="rpm-section__grid">
            <CustomInput
              inputLabel="Counter Party ID"
              type="text"
              placeholder="e.g. HUB"
              name="counterPartyId"
              onChangeValue={(_, value) => handleInputChange('counterPartyId', value as string)}
              handleBlur={() => handleBlur('counterPartyId')}
              value={formData.counterPartyId}
              characterLimit={3}
              error={Boolean(touched.counterPartyId && errors.counterPartyId)}
              errorMessage={touched.counterPartyId ? errors.counterPartyId : ''}
            />
            <SelectField
              inputLabel="Country Code"
              name="counterCountryCode"
              placeholder="e.g. DE"
              options={ISO_COUNTRY_CODES}
              selectedValue={formData.counterCountryCode}
              enableSearch
              onChangeValue={(name, value) => handleInputChange(name, value as string)}
              error={Boolean(touched.counterCountryCode && errors.counterCountryCode)}
              errorMessage={touched.counterCountryCode ? errors.counterCountryCode : ''}
            />
          </div>
          <div className="rpm-section__field">
            <SelectField
              inputLabel="Partner Role"
              name="counterRole"
              options={[
                { label: 'CPO', value: 'CPO' },
                { label: 'eMSP', value: 'EMSP' },
              ].filter((opt) => opt.value.toUpperCase() !== ourRole.toUpperCase())}
              selectedValue={formData.counterRole}
              onChangeValue={(name, value) => handleInputChange(name, value as string)}
              error={Boolean(touched.counterRole && errors.counterRole)}
              errorMessage={touched.counterRole ? errors.counterRole : ''}
            />
          </div>
        </div>

        {/* Sender-specific: Handshake Credentials */}
        {registrationType === 'SENDER' && (
          <div className="rpm-mode-section rpm-mode-section--sender">
            <h3 className="rpm-mode-section__title">Handshake Credentials</h3>
            <p className="rpm-mode-section__desc">
              Paste the Versions URL and Token A provided by your partner.
            </p>
            <div className="rpm-section__field">
              <CustomInput
                inputLabel="Partner Versions URL"
                type="text"
                placeholder="https://partner.com/ocpi/versions"
                name="theirVersionsUrl"
                onChangeValue={(_, value) => handleInputChange('theirVersionsUrl', value as string)}
                handleBlur={() => handleBlur('theirVersionsUrl')}
                value={formData.theirVersionsUrl}
                error={Boolean(touched.theirVersionsUrl && errors.theirVersionsUrl)}
                errorMessage={touched.theirVersionsUrl ? errors.theirVersionsUrl : ''}
              />
            </div>
            <div className="rpm-section__field">
              <CustomInput
                inputLabel="Partner Token A"
                type="text"
                placeholder="e.g. 1S54a6S86sA776s8f..."
                name="theirTokenA"
                onChangeValue={(_, value) => handleInputChange('theirTokenA', value as string)}
                handleBlur={() => handleBlur('theirTokenA')}
                value={formData.theirTokenA}
                error={Boolean(touched.theirTokenA && errors.theirTokenA)}
                errorMessage={touched.theirTokenA ? errors.theirTokenA : ''}
              />
            </div>
          </div>
        )}

        {/* Receiver-specific: Generate Invitation info */}
        {registrationType === 'RECEIVER' && (
          <div className="rpm-mode-section rpm-mode-section--receiver">
            <h3 className="rpm-mode-section__title">Generate Invitation</h3>
            <p className="rpm-mode-section__desc">
              You are generating an invitation. We will provide you with a <strong>Token A</strong>{' '}
              and a <strong>Versions URL</strong>. Share these with your partner so they can
              initiate the connection to our platform.
            </p>
          </div>
        )}

        <div className="rpm-form-actions">
          <MyButton label="Cancel" buttonType="outline" onClickFunc={onClose} />
          <MyButton
            label={buttonLabel}
            buttonType="primary"
            onClickFunc={handleSubmit}
            disableButton={loading || !isFormValid}
          />
        </div>
      </div>
    );
  };

  return (
    <ModalBox
      open={open}
      title="Establish OCPI Connection"
      content={renderFormContent()}
      closeModal={onClose}
      header
      rightCorner=""
    />
  );
}

export default RegisterPartnerModal;
